import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#060d1a',
      border: '1px solid rgba(129,140,248,0.25)',
      borderRadius: 16,
      padding: '12px 18px',
      boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
    }}>
      <p style={{ color: '#475569', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ color: '#fff', fontWeight: 900, fontSize: 20, lineHeight: 1 }}>
          {Number(payload[0].value).toLocaleString()}
        </span>
        <span style={{ color: '#818cf8', fontSize: 11, fontWeight: 700 }}>{currency}</span>
      </div>
    </div>
  );
};

const PulseDot = ({ cx, cy, index, dataLength }) => {
  if (index !== dataLength - 1 || !cx || !cy || isNaN(cx) || isNaN(cy)) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r="14" fill="#818cf8" fillOpacity="0">
        <animate attributeName="r" from="6" to="22" dur="2s" repeatCount="indefinite" />
        <animate attributeName="fill-opacity" from="0.35" to="0" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r="5" fill="#818cf8" />
      <circle cx={cx} cy={cy} r="2.5" fill="#fff" />
    </g>
  );
};

const ActivityChart = ({ data, devise, displayCurrency, title }) => {
  const chartData = data?.length > 0 ? data : [];
  const currency = displayCurrency(devise);

  const total = useMemo(() =>
    chartData.reduce((sum, d) => sum + (d.montant || 0), 0), [chartData]);

  return (
    <div style={{
      background: 'linear-gradient(160deg, #07101f 0%, #0b1628 60%, #0d1a35 100%)',
      borderRadius: '2rem',
      padding: '28px 24px 22px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 40px 80px rgba(0,0,0,0.25)',
    }}>
      {/* Ambient glows */}
      <div style={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -60, left: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(129,140,248,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      {/* Header */}
      <div style={{ marginBottom: 28, position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <p style={{ color: '#334155', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 6 }}>
            {title || 'Analyse Financière'}
          </p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ color: '#f8fafc', fontSize: 38, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.02em' }}>
              {total.toLocaleString()}
            </span>
            <span style={{ color: '#818cf8', fontSize: 14, fontWeight: 800 }}>{currency}</span>
          </div>
        </div>
        {chartData.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.2)', borderRadius: 999, padding: '5px 12px', marginTop: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8' }}>
              <style>{`@keyframes ss-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', animation: 'ss-blink 2s ease-in-out infinite' }} />
            </div>
            <span style={{ color: '#818cf8', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Live</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ height: 200, position: 'relative' }}>
        {chartData.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#334155', fontSize: 13, fontWeight: 700 }}>
            Aucune donnée pour cette période
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 4, left: -24, bottom: 0 }}>
              <defs>
                <linearGradient id="ssGradCA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.03)" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#334155', fontWeight: 800 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#334155' }}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
              />
              <Tooltip
                content={<CustomTooltip currency={currency} />}
                cursor={{ stroke: 'rgba(129,140,248,0.2)', strokeWidth: 1 }}
              />
              <Area
                type="monotone"
                dataKey="montant"
                stroke="#818cf8"
                strokeWidth={2.5}
                fill="url(#ssGradCA)"
                dot={(props) => <PulseDot {...props} dataLength={chartData.length} />}
                activeDot={{ r: 5, fill: '#818cf8', stroke: 'rgba(129,140,248,0.3)', strokeWidth: 6 }}
                animationDuration={1400}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Footer legend */}
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 2.5, background: 'linear-gradient(90deg, #6366f1, #818cf8)', borderRadius: 2 }} />
        <span style={{ color: '#334155', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Chiffre d'affaires</span>
      </div>
    </div>
  );
};

export default ActivityChart;
